import { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import api, { formatError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Brand } from "../components/Brand";
import { ShieldCheck, Loader2, CreditCard, CheckCircle2, ArrowRight, Lock } from "lucide-react";
import { toast } from "sonner";

const KIND_LABEL = {
  pro: "Mock2Crack Pro — Monthly", payg_ai: "AI Interview (single)", payg_expert: "Expert Interview (single)",
  college_starter: "College Starter — 100 credits", college_growth: "College Growth — 500 credits",
  agency_starter: "Agency Starter — 250 credits", agency_growth: "Agency Growth — 1000 credits",
};

export default function Checkout() {
  const [params] = useSearchParams();
  const kind = params.get("kind");
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [paying, setPaying] = useState(false);
  const [done, setDone] = useState(null);
  const [card, setCard] = useState({ number: "4242 4242 4242 4242", exp: "12/28", cvv: "123", name: user?.name || "" });

  useEffect(() => {
    api.post("/billing/checkout", { kind }).then((r) => setOrder(r.data))
      .catch((e) => { toast.error(formatError(e.response?.data?.detail)); navigate("/pricing"); });
  }, [kind, navigate]);

  const pay = async () => {
    setPaying(true);
    try {
      const { data } = await api.post("/billing/pay", { order_id: order.id, payment_method: "card" });
      await refreshUser();
      setDone(data);
      toast.success("Payment successful!");
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setPaying(false); }
  };

  useEffect(() => {
    if (!done) return;
    const AGENCY_R = ["agency_admin", "recruitment_manager", "recruiter"];
    const home = user?.role === "college_admin" ? "/college/billing" : AGENCY_R.includes(user?.role) ? "/agency/billing" : "/candidate/billing";
    const t = setTimeout(() => navigate(home), 4000);
    return () => clearTimeout(t);
  }, [done, user, navigate]);

  if (!order) return <div className="min-h-screen grid place-items-center"><Loader2 className="h-8 w-8 animate-spin text-brand" /></div>;

  if (done) {
    const AGENCY_R = ["agency_admin", "recruitment_manager", "recruiter"];
    const home = user?.role === "college_admin" ? "/college/billing" : AGENCY_R.includes(user?.role) ? "/agency/billing" : "/candidate/billing";
    return (
      <div className="min-h-screen bg-background grid place-items-center p-5">
        <div className="w-full max-w-md fade-up">
          <div className="bg-white rounded-2xl border border-slate-200 shadow-card p-8 text-center">
            <div className="h-16 w-16 rounded-2xl bg-emerald-100 grid place-items-center mx-auto mb-4"><CheckCircle2 className="h-8 w-8 text-emerald-600" /></div>
            <h1 className="font-heading text-2xl font-bold text-ink">Payment Successful</h1>
            <p className="text-ink-soft text-sm mt-1">{order.description}</p>
            <div className="mt-5 rounded-xl border border-slate-200 divide-y divide-slate-100 text-left">
              <div className="flex justify-between px-4 py-2.5 text-sm"><span className="text-ink-faint">Invoice</span><span className="num font-semibold text-brand">{done.invoice.id}</span></div>
              <div className="flex justify-between px-4 py-2.5 text-sm"><span className="text-ink-faint">Amount</span><span className="num font-semibold text-ink">{order.currency}{order.amount.toLocaleString()}</span></div>
              {done.effect?.credits_added && <div className="flex justify-between px-4 py-2.5 text-sm"><span className="text-ink-faint">Credits added</span><span className="num font-semibold text-emerald-600">+{done.effect.credits_added}</span></div>}
              {done.effect?.subscription && <div className="flex justify-between px-4 py-2.5 text-sm"><span className="text-ink-faint">Valid until</span><span className="num font-semibold text-ink">{done.effect.subscription.valid_until}</span></div>}
            </div>
            <div className="mt-6 flex gap-3">
              <Link to={`/invoice/${done.invoice.id}`} className="flex-1 text-sm font-bold text-ink bg-slate-100 hover:bg-slate-200 px-5 py-3 rounded-full transition-colors">View Invoice</Link>
              <button onClick={() => navigate(home)} className="flex-1 inline-flex items-center justify-center gap-2 text-sm font-bold text-white bg-brand hover:bg-brand-hover px-5 py-3 rounded-full transition-colors">Continue <ArrowRight className="h-4 w-4" /></button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <header className="glass border-b border-slate-200 px-5 h-16 flex items-center"><Brand /></header>
      <div className="max-w-4xl mx-auto px-5 py-12 grid lg:grid-cols-2 gap-8 fade-up">
        {/* summary */}
        <div>
          <h1 className="font-heading text-2xl font-bold text-ink mb-4">Order Summary</h1>
          <div className="bg-white rounded-2xl border border-slate-200 shadow-card p-6">
            <div className="flex justify-between items-start pb-4 border-b border-slate-100">
              <div><div className="font-semibold text-ink">{KIND_LABEL[kind] || order.description}</div><div className="text-xs text-ink-faint mt-0.5">Order {order.id}</div></div>
              <div className="num font-bold text-ink">{order.currency}{order.amount.toLocaleString()}</div>
            </div>
            <div className="flex justify-between pt-4 text-sm"><span className="text-ink-soft">Subtotal</span><span className="num text-ink">{order.currency}{order.amount.toLocaleString()}</span></div>
            <div className="flex justify-between pt-2 text-sm"><span className="text-ink-soft">Tax</span><span className="num text-ink">{order.currency}0</span></div>
            <div className="flex justify-between pt-4 mt-3 border-t border-slate-100 font-bold"><span className="text-ink">Total</span><span className="num text-brand text-lg">{order.currency}{order.amount.toLocaleString()}</span></div>
          </div>
          <div className="mt-4 flex items-center gap-2 text-xs text-ink-faint"><ShieldCheck className="h-4 w-4 text-emerald-500" /> Simulated secure payment · no real charge</div>
        </div>

        {/* payment */}
        <div>
          <h2 className="font-heading text-2xl font-bold text-ink mb-4">Payment</h2>
          <div className="bg-white rounded-2xl border border-slate-200 shadow-card p-6 space-y-4">
            <div className="flex items-center gap-2 text-sm font-semibold text-ink"><CreditCard className="h-4 w-4 text-brand" /> Card details</div>
            <input data-testid="card-number" value={card.number} onChange={(e) => setCard({ ...card, number: e.target.value })} className="w-full rounded-lg border border-slate-300 px-3.5 py-2.5 text-sm num focus:outline-none focus:ring-2 focus:ring-brand" placeholder="Card number" />
            <div className="grid grid-cols-2 gap-3">
              <input value={card.exp} onChange={(e) => setCard({ ...card, exp: e.target.value })} className="rounded-lg border border-slate-300 px-3.5 py-2.5 text-sm num focus:outline-none focus:ring-2 focus:ring-brand" placeholder="MM/YY" />
              <input value={card.cvv} onChange={(e) => setCard({ ...card, cvv: e.target.value })} className="rounded-lg border border-slate-300 px-3.5 py-2.5 text-sm num focus:outline-none focus:ring-2 focus:ring-brand" placeholder="CVV" />
            </div>
            <input value={card.name} onChange={(e) => setCard({ ...card, name: e.target.value })} className="w-full rounded-lg border border-slate-300 px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand" placeholder="Name on card" />
            <button data-testid="pay-now" onClick={pay} disabled={paying} className="w-full inline-flex items-center justify-center gap-2 text-sm font-bold text-white bg-brand hover:bg-brand-hover px-5 py-3.5 rounded-full shadow-ambient disabled:opacity-60 transition-colors">
              {paying ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Lock className="h-4 w-4" /> Pay {order.currency}{order.amount.toLocaleString()}</>}
            </button>
            <p className="text-[11px] text-center text-ink-faint">By paying you agree to Mock2Crack's terms. This is a simulated transaction.</p>
          </div>
        </div>
      </div>
    </div>
  );
}
